import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ISignin } from '../types/auth.types';
import { Input } from '../../common/components/form/input.component';

import {
  FormCaption,
  FormSubmit,
  InformSpan,
  LinkText
} from '../../common/components';
import { EmailErrors, AUTH_PATTERN, PasswordErrors } from './validation.pattern';
import { ROUTER_LINKS } from '../../common/consts/app-keys.const';
import { APP_KEYS } from '../../common/consts';
import { authService } from '../service/auth.service';
import useAuth from '../feature/auth';
import { useThrottle } from '../../hooks/useThrottle';
import { resolveReturnTo } from '../feature/return-to';
import { LockInputIcon } from '../../common/components/icons/lock-input-icon.component';
import { EmailInputIcon } from '../../common/components/icons/email-input-icon.component';

type Inputs = {
  email: string;
  password: string;
};

export const Signin = () => {
  const { t } = useTranslation();
  const { setIsAuthenticated, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLockedOut, setIsLockedOut] = React.useState(false);
  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isValid }
  } = useForm<Inputs>({
    mode: 'onChange'
  });

  const signin = useMutation({
    mutationKey: [APP_KEYS.QUERY_KEYS.USER],
    mutationFn: async (data: ISignin) => authService.login(data),
    onSuccess: async () => {
      setIsAuthenticated(true);
      await checkAuth();
      const target = resolveReturnTo(searchParams.get('returnTo'));
      if (target?.kind === 'external') {
        // Another Squarebit app (e.g. Riser) sent them here; it is a different
        // host, so the router cannot take them back.
        window.location.assign(target.url);
        return;
      }
      navigate(target?.kind === 'route' ? target.path : ROUTER_LINKS.ROOT);
    },
    onError: (err: any) => {
      const message = err.response?.data?.message ?? '';
      if (
        message.includes('Too many failed attempts') ||
        err.response?.status === 429
      ) {
        setIsLockedOut(true);
        setError('root', {
          type: 'manual',
          message: t('auth.tooManyAttempts')
        });
      } else if (
        message.includes('User not found') ||
        message.includes('Account with this email')
      ) {
        setError('email', {
          type: 'manual',
          message: t('auth.accountNotFound')
        });
      } else if (message.includes('Credentials incorrect')) {
        setError('password', {
          type: 'manual',
          message: t('auth.passwordIncorrect')
        });
      } else {
        setError('root', {
          type: 'manual',
          message: message || t('auth.genericError')
        });
      }
    }
  });
  const throttledSignin = useThrottle(signin.mutate);

  const onSubmit: SubmitHandler<Inputs> = (data: ISignin) => {
    throttledSignin(data);
  };

  const reason = searchParams.get('reason');

  return (
    <div>
      <FormCaption>{t('auth.loginTitle')}</FormCaption>

      {reason === 'download' ? (
        <div className="bg-blue-50 border border-blue-200 rounded-[10px] px-[16px] py-[12px] mb-[16px] text-[13px] text-blue-800">
          {t('auth.signInOrCreate')}
        </div>
      ) : (
        <InformSpan className="hidden md:block">
          {t('auth.loginSubtitle')}
        </InformSpan>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
        <Input
          errors={errors}
          name="email"
          label={t('auth.email')}
          placeholder={t('auth.enterHere')}
          type="email"
          icon={<EmailInputIcon />}
          register={register.bind(null, 'email', {
            required: { message: EmailErrors.required, value: true },
            pattern: {
              value: AUTH_PATTERN.email,
              message: EmailErrors.pattern
            }
          })}
        />

        <Input
          errors={errors}
          name="password"
          label={t('auth.password')}
          placeholder={t('auth.enterHere')}
          type="password"
          icon={<LockInputIcon />}
          register={register.bind(null, 'password', {
            required: { message: PasswordErrors.required, value: true }
          })}
        />
        {errors.root && (
          <p className="text-red-500 font-body text-[14px] mb-4">
            {errors.root.message}
          </p>
        )}
        <div className="flex justify-end items-center max-w-[301px] md:max-w-[392px] mb-[22px] md:mb-[24px]">
          <Link
            to={ROUTER_LINKS.FORGOT_PASSWORD}
            className="text-base-black font-body text-[16px] font-medium hover:text-blue-500 active:text-blue-600"
          >
            {t('auth.forgotPassword')}
          </Link>
        </div>

        <FormSubmit
          isDisabled={!isValid || isLockedOut}
          isLoading={signin.isPending}
          value={t('auth.loginButton')}
        />

        <LinkText
          linkText={t('auth.createAccount')}
          to={
            reason ? `${ROUTER_LINKS.SIGNUP}?reason=${reason}` : ROUTER_LINKS.SIGNUP
          }
          text={t('auth.newToStore')}
        />
      </form>
    </div>
  );
};
